import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Mail, ArrowRight, ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { useAnonymousReviewContext } from './AnonymousReviewProvider';
import { trackAnonymousReviewConversion } from '../../lib/analytics';

const AnonymousStep1: React.FC = () => {
  const navigate = useNavigate();
  const { data, updateData, error, setError, validateStep1 } = useAnonymousReviewContext();
  const [isUploading, setIsUploading] = useState(false);

  // Track conversion when component mounts (user visits anonymous review flow)
  useEffect(() => {
    trackAnonymousReviewConversion();
    
    // Check if user came from landing page via URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const fromLandingPage = urlParams.get('fromLandingPage');
    const title = urlParams.get('title');
    
    if (fromLandingPage === 'true' && title) {
      console.log('User came from landing page with title:', title);
      updateData({ 
        title: decodeURIComponent(title),
        hasFilesFromLandingPage: true 
      });
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
  ];

  const handleFileUpload = (uploadedFiles: FileList) => {
    console.log('File upload triggered:', uploadedFiles);
    // Filter out unsupported files
    const fileArray = Array.from(uploadedFiles).filter(file => SUPPORTED_FILE_TYPES.includes(file.type));
    updateData({ files: fileArray });
    
    // Auto-generate title from first filename
    if (fileArray.length > 0) {
      const fileName = fileArray[0].name;
      const title = fileName.replace(/\.[^/.]+$/, ''); // Remove file extension
      console.log('Auto-generated title:', title);
      updateData({ title });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = data.files.filter((_, i) => i !== index);
    updateData({ files: newFiles });
  };

  const handleContinue = () => {
    if (validateStep1()) {
      navigate('../step-2');
    }
  };

  const getCurrentDate = () => new Date().toISOString().split('T')[0];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Try Approvia - Create Review
        </h1>
        <p className="text-muted-foreground">
          Step 1 of 3: Upload your document and provide basic information
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Progress</span>
          <span className="text-sm text-muted-foreground">1 of 3</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-primary h-2 rounded-full" style={{ width: '33%' }}></div>
        </div>
      </div>

      <div className="grid gap-6">
        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Document
            </CardTitle>
            <CardDescription>
              Upload the document you want to be reviewed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                data.files.length > 0 ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">Drop your files here</p>
              <p className="text-muted-foreground mb-4">
                or click to browse your files
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Supports: PDF, Word, PowerPoint, Images (JPG, PNG, GIF)
              </p>
              {data.files.length === 0 && (data.title || data.fileMetadata?.length) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    {data.hasFilesFromLandingPage 
                      ? "📁 Great! You've selected a file on our landing page. Please re-upload it here to continue."
                      : "📁 Your form data is saved! Files need to be re-uploaded due to browser security."
                    }
                  </p>
                  {data.fileMetadata && data.fileMetadata.length > 0 && (
                    <div className="space-y-1 mb-2">
                      <p className="text-xs text-blue-700 font-medium">Previously selected files:</p>
                      {data.fileMetadata.map((file, index) => (
                        <div key={index} className="text-xs text-blue-700">
                          • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                      ))}
                    </div>
                  )}
                  {data.hasFilesFromLandingPage && (
                    <div className="mt-2">
                      <p className="text-xs text-blue-700 font-medium">Title from landing page:</p>
                      <p className="text-xs text-blue-700">{data.title}</p>
                    </div>
                  )}
                  <p className="text-xs text-blue-700">
                    Re-upload the same files above, or continue - files will be uploaded during review creation.
                  </p>
                </div>
              )}
              <input
                type="file"
                multiple
                accept=".doc,.docx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.gif"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                className="hidden"
                id="file-upload"
              />
              <Button 
                variant="outline" 
                onClick={() => document.getElementById('file-upload')?.click()}
                type="button"
              >
                Choose Files
              </Button>
            </div>

            {data.files.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="font-medium">Selected Files:</h4>
                {data.files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="text-sm">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeFile(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Review Information
            </CardTitle>
            <CardDescription>
              Provide basic information about your review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Review Title *</Label>
              <Input
                id="title"
                value={data.title}
                onChange={(e) => updateData({ title: e.target.value })}
                placeholder="Enter review title"
                className="mt-1"
              />
            </div>


          </CardContent>
        </Card>



        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-600">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <Button onClick={handleContinue} disabled={isUploading}>
            {data.files.length === 0 && data.title ? 'Continue (Upload Later)' : 'Continue to Step 2'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnonymousStep1; 